import type { KatInput } from "@amzn/katal-react";
import { isEmpty } from "lodash-es";
import React, { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { FiltersDefinition } from "src/components/Contexts/FilterOptions/definition";
import { useFilterOptions } from "src/components/Contexts/FilterOptions/Provider";
import {
  DomainName,
  FilterActions,
} from "src/components/Contexts/FilterOptions/types";
import SearchBar from "src/components/SearchBar";
import {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "src/components/TranslatedField/TranslationKey";
import styles from "src/styles.module.scss";
import { updateUrlParams } from "src/utils";

const MAX_TERMS = 5;

const DocumentListingSearchBar: FC = () => {
  const history = useHistory();
  const { t } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );

  const {
    state: { selectedValues: filter },
    dispatch,
  } = useFilterOptions();
  const [shouldShowWarning, setShouldShowWarning] = useState(false);

  const exceedMaxTerms = (searchValue?: string) => {
    const terms = searchValue
      ?.trim()
      .split(" ")
      .filter((letter: string) => letter);
    return terms!.length > MAX_TERMS;
  };

  const search = (newSearchValue?: string) => {
    if (shouldShowWarning && !isEmpty(newSearchValue)) {
      console.warn("Exceed max terms");
      return;
    }

    updateUrlParams([[DomainName.SEARCH, newSearchValue]], history);

    dispatch({
      type: FilterActions.SET_SELECTED_VALUES,
      payload: {
        domainName: DomainName.SEARCH,
        value: newSearchValue,
      },
    });

    setShouldShowWarning(exceedMaxTerms(newSearchValue));
  };

  const onKeyup = (e: KatInput.KeydownEvent) => {
    setShouldShowWarning(exceedMaxTerms(e.currentTarget.value));
  };

  const handleSearchChange = (searchPattern: string) => {
    search(searchPattern);
  };

  const handleClearSearch = () => {
    search(
      FiltersDefinition[DomainName.SEARCH].defaultSelectedValues as string
    );
  };

  const shouldShowClearButton = () => {
    return !!filter.search;
  };

  return (
    <SearchBar
      title={t("aic_search_document_title")}
      placeholder={t("aic_search_document_place_holder")}
      searchablePattern={filter.search as string}
      onChange={handleSearchChange}
      onClear={handleClearSearch}
      onKeyupInput={onKeyup}
      showClearButton={shouldShowClearButton()}
      classes={[styles.mr_8]}
      shouldShowWarning={shouldShowWarning}
      warningMsg={t("aic_search_warning_msg")}
      disableSearch={shouldShowWarning}
    />
  );
};

export default DocumentListingSearchBar;
